/**
 * Switching our card option on.
 *
 * There is nothing to configure — the accounts are ours and the keys never leave the server. The
 * only decision here is whether to offer clients a card button beside the bank details the agency
 * already publishes.
 *
 * **A provider we cannot charge through is absent, not disabled.** The list arrives from the
 * server already filtered to what has been proven to work, and with nothing in it this card does
 * not render at all. An agency has no business reading about our deployment's configuration.
 */

import { useQuery } from "@tanstack/react-query";
import { DEFAULT_PLATFORM_FEE, formatRate } from "@rentorng/core";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { Alert, Card, CardTitle, Switch } from "@/components/ui";

interface PlatformGateway {
  provider: string;
  name: string;
  currencies: string[];
  enabled: boolean;
  label: string;
  /** True when the agency has its own account configured for this provider. */
  usingOwnAccount: boolean;
}

export function CardPayments({ readOnly, step }: { readOnly: boolean; step?: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ["platform-gateways"],
    queryFn: () => api.get<{ gateways: PlatformGateway[]; canEdit: boolean }>("/settings/platform-gateways"),
  });

  const toggle = useApiMutation({
    mutationFn: (input: { provider: string; enabled: boolean }) =>
      api.post(`/settings/platform-gateways/${input.provider}`, { enabled: input.enabled }),
    invalidates: [["platform-gateways"]],
  });

  // Nothing we can charge through: the card goes entirely rather than explaining itself.
  if (isLoading) return null;
  if (!data?.gateways.length) return null;

  return (
    <Card>
      <CardTitle
        hint={
          <>
            Our accounts, so there is nothing to set up. Money collected this way reaches you in a settlement rather
            than landing in your bank directly, less {formatRate(DEFAULT_PLATFORM_FEE.bp)} and the provider's fee.
          </>
        }
      >
        {step ? <span className="mr-2 text-ink-400">Step {step}</span> : null}
        Card and online payments
      </CardTitle>

      {toggle.errorMessage ? <Alert tone="danger">{toggle.errorMessage}</Alert> : null}

      <ul className="space-y-2">
        {data.gateways.map((gateway) => {
          return (
            <li
              key={gateway.provider}
              className={`flex items-center gap-3 rounded-[var(--radius-surface)] border p-3 transition-colors ${
                gateway.enabled ? "border-ink-200 bg-white" : "border-ink-200 bg-ink-50"
              }`}
            >
              {/* On a white chip, so each mark shows in its own colours rather than being
                  recoloured to fit ours. Greyed only while the option is off. */}
              <span
                className={`flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-control)] border border-ink-100 bg-white ${
                  gateway.enabled ? "" : "opacity-45 grayscale"
                }`}
              >
                {/*
                  The provider's own artwork, trimmed and centred at build time from the file in
                  `assets/brand/sources/`. Traced approximations were tried first and were not
                  good enough: a logo that is nearly right is more noticeable than one that is
                  missing, and it is somebody else's trademark to get wrong.
                */}
                <img src={`/brand/${gateway.provider}.png`} alt="" width={26} height={26} />
              </span>

              <span className="min-w-0 flex-1">
                <span className={`block text-sm font-semibold ${gateway.enabled ? "text-ink-900" : "text-ink-600"}`}>
                  {gateway.name}
                </span>
                <span className="block truncate text-xs text-ink-500">{gateway.currencies.join(" · ")}</span>
                {gateway.usingOwnAccount ? (
                  <span className="mt-0.5 block text-xs text-warn-700">
                    Takes over from your own {gateway.name} account.
                  </span>
                ) : null}
              </span>

              <Switch
                checked={gateway.enabled}
                disabled={readOnly || toggle.isPending}
                label={`Offer ${gateway.name}`}
                onChange={(enabled) => toggle.mutate({ provider: gateway.provider, enabled })}
              />
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
